import { useState, useEffect, SyntheticEvent } from "react";
import axios from "axios";
import ClientHistoryModal from "./modals/ClientHistoryModal";
import ClientChangeHistoryModal from "../../components/common/ClientChangeHistoryModal";
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import NotificationImportantOutlined from '@mui/icons-material/NotificationImportantOutlined';
import { ConfirmDialog, DateField, StatCard, type DataTableColumn } from "@routes-plan/ui";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString, operationalTimezone } from '../../utils/operationalDateTime';
import { useResourcePermissions } from "../../hooks/useResourcePermissions";
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { formatCurrency as companyMoney } from '../../utils/format/currency';
import {
  OfficeSelect,
  ReportEmpty,
  ReportFilters,
  ReportPage,
  ReportResults,
  ReportStats,
  ReportTable,
  RouteMultiSelect,
  RowAction,
} from "./components/ReportLayout";
import { accessErrorMessage } from '../../utils/accessErrors';

interface LoanRow {
  id: number;
  route: string;
  routeId: number;
  idNumber: string;
  clientName: string;
  phone: string;
  backups: Array<{ id: number }>;
  backupAlertActive: boolean;
  termDays: number;
  interestRate: number;
  value: number;
  totalValue: number;
  interestValue: number;
  insuranceCharge: number;
  paidValue: number;
  contract: string;
  statusDate: string;
  date: string;
  loanDateYmd: string;
  loanDateTs: number;
  routeName: string;
  fullClient: any;
}

// Interfaces mejoradas
interface Office {
  id: number;
  name: string;
}

interface Route {
  id: number;
  name: string;
}

interface Client {
  id: number;
  idNumber: string;
  fullName: string;
  phone: string;
  backupAlertActive?: boolean;
  backups?: Array<{ id: number }>;
}

interface Loan {
  id: number;
  date: string;
  statusDate: string;
  contract?: string;
  value: number;
  totalValue: number;
  termDays: number;
  interestRate: number;
  interestValue: number;
  insuranceCharge: number;
  paidValue: number;
  client: Client;
  route?: {
    id: number;
    name: string;
  };
}

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;
  selectedRouteIds: number[];
}

interface ReportePrestamoFechaProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Funciones utilitarias
const formatCurrency = (value: number): string => {
  return companyMoney(value);
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "00/00/0000";
  const isDateTime = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateString);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isDateTime
    ? moment.tz(dateString, "YYYY-MM-DD HH:mm:ss", operationalTimezone())
    : isDateOnly
    ? moment.tz(dateString, "YYYY-MM-DD", operationalTimezone())
    : moment.tz(dateString, operationalTimezone());

  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "00/00/0000";
};




// Componente principal con lógica mejorada
const LoansByDate: React.FC<ReportePrestamoFechaProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  setPageTitle('menu.reports.loans-by-date');
  const currentDate = nowOperationalDateString();
  const [datas, setDatas] = useState<any[]>([]);

  // Estados
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedRouteIds: [],
  });
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [rows, setRows] = useState<LoanRow[]>([]);
  const [queried, setQueried] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalLoans: 0,
    count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openChangeModal, setOpenChangeModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedClientForChanges, setSelectedClientForChanges] = useState<any>(null);
  const { canRead } = useResourcePermissions('/reports/loans-by-date');
  const { canManageBackupAlert } = useResourcePermissions('/reports/client-history');

  // Efecto para cargar oficinas al montar el componente
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await axios.get("/offices");
        if (response.data && response.data.data) {
          setOffices(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching offices:", error);
        showAlert(t('loanReports.officesLoadError'), "error");
      }
    };

    fetchOffices();
  }, [showAlert, t]);

  // Efecto para cargar cobros cuando cambia la oficina
  useEffect(() => {
    const fetchRoutes = async () => {
      if (!filter.officeId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/offices/${filter.officeId}/routes`);
        if (response.data) {
          setOfficeRoutes(response.data);
          // Al cambiar la oficina se limpian las rutas elegidas
          setFilter((prev) => ({ ...prev, selectedRouteIds: [] }));
        }
      } catch (error) {
        console.error("Error fetching routes:", error);
        showAlert(t('loanReports.routesLoadError'), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [filter.officeId, showAlert, t]);

  const validateForm = (): boolean => {
    if (filter.selectedRouteIds.length === 0) {
      showAlert(t('loanReports.selectAtLeastOneRoute'), "warning");
      return false;
    }

    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t('loanReports.invalidDateRange'), "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    setQueried(true);

    try {
      const response = await axios.post("/reports/loans-by-date", {
        startDate: filter.startDate,
        endDate: filter.endDate,
        routeId: filter.selectedRouteIds,
      });
      setDatas(response.data.data);
      const loans: Loan[] = response.data.data;
      if (!loans || loans.length === 0) {
        showAlert(t('loanReports.loansByDate.empty'), "info");
        setRows([]);
        setSummary({
          totalPayments: 0,
          totalLoans: 0,
          count: 0,
        });
        return;
      }

      const sortedLoans = [...loans].sort((a, b) => {
        const routeCompare = String(a.route?.name || "").localeCompare(
          String(b.route?.name || ""),
          "es",
          { sensitivity: "base" }
        );
        if (routeCompare !== 0) return routeCompare;

        return moment(b.date).valueOf() - moment(a.date).valueOf();
      });

      const mappedRows: LoanRow[] = sortedLoans
        .map((loan) => {
          const loanMoment = moment.tz(loan.date, operationalTimezone());
          return {
            id: loan.id,
            route: loan.route?.name || "",
            routeId: loan.route?.id || 0,
            idNumber: loan.client.idNumber,
            clientName: loan.client.fullName,
            phone: loan.client.phone,
            backups: loan.client?.backups || [],
            backupAlertActive:
              Boolean(loan.client?.backupAlertActive) &&
              Array.isArray(loan.client?.backups) &&
              loan.client.backups.length > 0,
            termDays: loan.termDays,
            interestRate: Number(loan.interestRate ?? 0),
            value: loan.value,
            totalValue: loan.totalValue,
            interestValue: Number(loan.interestValue ?? 0),
            insuranceCharge: Number(loan.insuranceCharge ?? 0),
            paidValue: loan.paidValue,
            contract: loan.contract || "",
            statusDate: formatDate(loan.statusDate),
            date: formatDate(loan.date),
            loanDateYmd: loanMoment.isValid() ? loanMoment.format("YYYY-MM-DD") : "",
            loanDateTs: loanMoment.isValid() ? loanMoment.valueOf() : 0,
            routeName: loan.route?.name || "",
            fullClient: loan.client,
          };
        });

      setRows(mappedRows);
      showAlert(t('loanReports.loansByDate.found', { count: loans.length }), "success")
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      showAlert(t('loanReports.loansByDate.loadError'), "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const handleDeleteLoan = async (loanId: number) => {
    if (!loanId) return;
    // La confirmación la pide el ConfirmDialog antes de llegar aquí.
    showLoading();
    try {
      await axios.post('/loans/bulk-delete', { ids: [loanId] });
      showAlert(t('loanReports.loansByDate.deletedOk'), 'success');
      setRows((prev) => prev.filter((row) => row.id !== loanId));
    } catch (error) {
      console.error('Error eliminando préstamo:', error);
      showAlert(accessErrorMessage(error) ?? t('common.deleteError'), 'error');
    } finally {
      hideLoading();
    }
  };

  // Totales del periodo (los mismos que antes iban en la fila de total)
  const totalValue = rows.reduce((sum, row) => sum + Number(row.value ?? 0), 0);
  const totalTotalValue = rows.reduce((sum, row) => sum + Number(row.totalValue ?? 0), 0);
  const totalInsuranceCharge = rows.reduce((sum, row) => sum + Number(row.insuranceCharge ?? 0), 0);

  const handleOpenModal = (row: LoanRow) => {
    const client = row.fullClient || {};
    setSelectedClient({
      routeId: row.routeId || 0,
      cedula: client.idNumber?.toString() || row.idNumber?.toString() || "",
      nombre: client.fullName || row.clientName || "",
      direccion: client.mainAddress || "",
      telefono: client.phone || row.phone || "",
      oficio: client.job || "",
      calificacion: "",
      celular: client.mobilePhone || "",
      id: client.id || row.fullClient?.id || 0,
      backupAlertActive: Boolean(row.backupAlertActive),
      backups: row.backups || [],
    });
    setOpenModal(true);
  };

  const columns: DataTableColumn<LoanRow>[] = [
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, sortable: true },
    { id: 'idNumber', header: t('common.idNumber'), value: (r) => r.idNumber, sortable: true, hideBelow: 'sm' },
    { id: 'clientName', header: t('loanReports.client'), value: (r) => r.clientName, sortable: true },
    { id: 'phone', header: t('common.phone'), value: (r) => r.phone, hideBelow: 'md' },
    { id: 'contract', header: t('loanReports.contractNumber'), value: (r) => r.contract, hideBelow: 'md' },
    { id: 'termDays', header: t('loanReports.term'), value: (r) => r.termDays, align: 'right', sortable: true },
    { id: 'interestRate', header: t('lend.utility'), value: (r) => Number(r.interestRate ?? 0), align: 'right', sortable: true },
    { id: 'insuranceCharge', header: t('loanReports.managementFee'), value: (r) => Number(r.insuranceCharge ?? 0), render: (r) => formatCurrency(Number(r.insuranceCharge ?? 0)), align: 'right', sortable: true },
    { id: 'totalValue', header: t('loanReports.totalValue'), value: (r) => Number(r.totalValue ?? 0), render: (r) => formatCurrency(Number(r.totalValue ?? 0)), align: 'right', sortable: true },
    { id: 'date', header: t('loanReports.loanDate'), value: (r) => r.loanDateTs, render: (r) => r.date, csv: (r) => r.date, sortable: true },
    { id: 'statusDate', header: t('loanReports.payoffDate'), value: (r) => r.statusDate, hideBelow: 'sm' },
  ];

  return (
    <ReportPage title={t('loanReports.loansByDate.title')} description={t('loanReports.loansByDate.description')}>
      <ReportFilters
        onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)}
        submitDisabled={loading}
        submitButtonProps={{ 'data-tour': 'loans-by-date-query' }}
      >
        <DateField
          label={t('loanReports.startDate')}
          data-tour="loans-by-date-start"
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? '' }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t('loanReports.endDate')}
          data-tour="loans-by-date-end"
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? '' }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={filter.officeId} onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))} tourId="loans-by-date-office" />
        <RouteMultiSelect
          routes={officeRoutes}
          tourId="loans-by-date-routes"
          value={filter.selectedRouteIds.map(String)}
          onChange={(ids) => setFilter((prev) => ({ ...prev, selectedRouteIds: ids.map(Number) }))}
        />
      </ReportFilters>

      {rows.length > 0 && (
        <ReportStats tourId="loans-by-date-stats">
          <StatCard label={t('loanReports.loansByDate.count')} value={rows.length} />
          <StatCard label={t('loanReports.loansByDate.principal')} value={formatCurrency(totalValue)} />
          <StatCard label={t('loanReports.totalValue')} value={formatCurrency(totalTotalValue)} />
          <StatCard label={t('loanReports.managementFee')} value={formatCurrency(totalInsuranceCharge)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<LoanRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading && queried}
          exportFileName={t('loanReports.loansByDate.exportFile')}
          tourId="loans-by-date"
          ariaLabel={t('loanReports.loansByDate.title')}
          emptyState={<ReportEmpty queried={queried} />}
          rowActions={(row) => (
            <>
              <RowAction
                allowed={canRead}
                deniedTooltip={t('loanReports.noPermissionHistory')}
                label={t('loanReports.history')}
                icon={<HistoryOutlined fontSize="small" />}
                onClick={() => handleOpenModal(row)}
              />
              {row.backupAlertActive && (
                <RowAction
                  allowed={canRead}
                  deniedTooltip={t('loanReports.noPermissionClientChanges')}
                  label={t('loanReports.viewClientChanges')}
                  color="warning"
                  icon={<NotificationImportantOutlined fontSize="small" />}
                  onClick={() => {
                    const client = row.fullClient || {};
                    setSelectedClientForChanges({
                      id: client.id || 0,
                      backupAlertActive: Boolean(row.backupAlertActive),
                      backups: row.backups || [],
                    });
                    setOpenChangeModal(true);
                  }}
                />
              )}
              {row.loanDateYmd === currentDate && (
                <RowAction
                  label={t('common.delete')}
                  color="error"
                  icon={<DeleteOutlined fontSize="small" />}
                  onClick={() => setPendingDelete(row.id)}
                />
              )}
            </>
          )}
        />
      </ReportResults>

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('loanReports.loansByDate.confirmDeleteTitle')}
        message={t('loanReports.loansByDate.confirmDelete')}
        confirmLabel={t('common.delete')}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const id = pendingDelete;
          setPendingDelete(null);
          if (id) void handleDeleteLoan(id);
        }}
      />
      <ClientHistoryModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setRows((current) =>
            current.map((row) =>
              Number(row.fullClient?.id || 0) === clientId
                ? {
                    ...row,
                    backupAlertActive: active && Array.isArray(row.backups) && row.backups.length > 0,
                    fullClient: {
                      ...(row.fullClient || {}),
                      backupAlertActive: active,
                    },
                  }
                : row
            )
          );
          setSelectedClient((current: any) =>
            current && Number(current.id || 0) === clientId
              ? { ...current, backupAlertActive: active }
              : current
          );
        }}
        clientData={selectedClient}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ClientChangeHistoryModal
        open={openChangeModal}
        onClose={() => setOpenChangeModal(false)}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setRows((current) =>
            current.map((row) =>
              Number(row.fullClient?.id || 0) === clientId
                ? {
                    ...row,
                    backupAlertActive: active && Array.isArray(row.backups) && row.backups.length > 0,
                    fullClient: {
                      ...(row.fullClient || {}),
                      backupAlertActive: active,
                    },
                  }
                : row
            )
          );
          setSelectedClientForChanges((current: any) =>
            current && Number(current.id || 0) === clientId
              ? { ...current, backupAlertActive: active }
              : current
          );
        }}
        clientData={selectedClientForChanges}
      />
    </ReportPage>
  );
};

export default LoansByDate;
